import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import usePutData from "../../../CustomHooks/useAxios/usePutData";
import { FACTORY_CHECKPOINT_UPDATE_REVIEW } from "../../../api/allApi_List";
import Modal from "../../../components/portal/Modal";
import GrayButton from "../../../components/UI/buttonUI/GrayButton";
import PrimaryButton from "../../../components/UI/buttonUI/PrimaryButton";
import { handleErrMessage } from "../../../functions/handleErrMessage";
import ErrorDisplay from "../../../components/UI/ErrorDisplay";
import CustomInput from "../../../components/UI/PageInput/CustomInput";
import { noSpaceOnlyValidation } from "../../../validations/formValidations";

const ReviewCheckpointModal = ({
  reviewId,
  name,
  description,
  setOpenReview,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();
  const { executePut, loading, formError, setFormError, successResponse } =
    usePutData();

  const onSubmit = (data) => {
    const remarks = [data.remarks];
    executePut(`${FACTORY_CHECKPOINT_UPDATE_REVIEW}`, {
      id: reviewId,
      status: "reviewed",
      remarks: remarks,
    });
  };

  const handleClose = () => {
    if (loading) return;
    if (successResponse) {
      navigate(0); // or better: refetch data
    } else {
      setOpenReview(false);
    }
  };

  return (
    <>
      <Modal
        onClose={handleClose}
        closeIcon={true}
        modalWidth="w-[95%] max-w-xl"
      >
        <div className="space-y-5 p-4">
          {/* ERROR VIEW */}
          {formError && (
            <ErrorDisplay
              message={handleErrMessage(formError)}
              closeAction={() => setFormError("")}
            />
          )}
          {/* SUCCESS VIEW */}
          {successResponse ? (
            <div className="flex flex-col items-center text-center">
              {/* Success Icon */}
              <Check color="#00a36c" strokeWidth={3} size={35} />
              {/* Success Title */}
              <h3 className="text-lg font-semibold text-green-3">
                Successfully Reviewed
              </h3>

              {/* Context Info */}
              <p className="text-sm text-stone-600 max-w-[300px] my-3">
                The checkpoint{" "}
                <span className="font-medium text-stone-800">"{name}"</span> has
                been successfully reviewed.
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div>
                <h2 className="text-lg font-semibold text-stone-800">
                  Review Checkpoint
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  Please confirm the review of the following checkpoint.
                </p>
              </div>

              {/* Checkpoint Info Card */}
              <div className="border border-stone-200 rounded-lg p-3 bg-gray-50 space-y-2">
                <div>
                  <h3 className="font-semibold text-stone-800">{name}</h3>
                  {description && (
                    <p className="text-xs text-stone-500 mt-1">{description}</p>
                  )}
                </div>

                <div className="bg-white border rounded-md p-3 text-xs text-stone-600 space-y-1">
                  <p>
                    This checkpoint has been marked and requires your review.
                  </p>
                  <p>
                    Once reviewed, the system will update its status to
                    <span className="font-medium text-green-600">
                      {" "}
                      Reviewed
                    </span>
                    .
                  </p>
                </div>
              </div>

              {/* Warning / Confirmation Note */}
              <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-3">
                Please ensure all details are verified before confirming this
                action.
              </div>
              <form
                onSubmit={
                  !loading ? (e) => handleSubmit(onSubmit)(e) : undefined
                }
              >
                <CustomInput
                  type="text"
                  name="remarks"
                  labelText="Remark*"
                  placeholder="Enter"
                  register={{
                    ...register("remarks", {
                      required: "Please enter remark",
                      ...noSpaceOnlyValidation,
                    }),
                  }}
                  error={errors.remarks}
                />
                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-2">
                  <GrayButton btnText="Cancel" onClick={handleClose} />
                  <PrimaryButton
                    btnText={!loading ? "Confirm" : "Processing..."}
                    loading={loading}
                  />
                </div>
              </form>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ReviewCheckpointModal;
